import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { Calendar, Clock, ArrowRight, Search, Tag } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
const blogPosts = [{
  id: 1,
  title: '5 Essential Tips for Maintaining Healthy Teeth Between Visits',
  excerpt: 'Discover the daily habits that can transform your oral health and keep your smile bright between dental appointments.',
  category: 'Oral Health',
  date: 'June 15, 2026',
  readTime: '5 min read',
  image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&q=80&w=800',
  featured: true
}, {
  id: 2,
  title: 'Understanding Invisalign: Is It Right for You?',
  excerpt: 'Learn about the benefits of clear aligners and how they compare to traditional braces for achieving your perfect smile.',
  category: 'Orthodontics',
  date: 'June 10, 2026',
  readTime: '7 min read',
  image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&q=80&w=800',
  featured: true
}, {
  id: 3,
  title: 'The Complete Guide to Dental Implants',
  excerpt: 'Everything you need to know about dental implants, from the procedure to recovery and long-term care.',
  category: 'Restorative',
  date: 'June 5, 2026',
  readTime: '10 min read',
  image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&q=80&w=800',
  featured: false
}, {
  id: 4,
  title: 'How to Prepare Your Child for Their First Dental Visit',
  excerpt: "Make your child's first dental experience positive and stress-free with these expert tips.",
  category: 'Pediatric',
  date: 'May 28, 2026',
  readTime: '4 min read',
  image: 'https://images.unsplash.com/photo-1606811841689-23dfddce3e95?auto=format&fit=crop&q=80&w=800',
  featured: false
}, {
  id: 5,
  title: 'Teeth Whitening: Professional vs At-Home Options',
  excerpt: 'Compare professional whitening treatments with at-home solutions to find the best option for your smile.',
  category: 'Cosmetic',
  date: 'May 20, 2026',
  readTime: '6 min read',
  image: 'https://images.unsplash.com/photo-1588776814546-1ffcf47267a5?auto=format&fit=crop&q=80&w=800',
  featured: false
}, {
  id: 6,
  title: 'Understanding Dental Insurance: Maximizing Your Benefits',
  excerpt: 'Navigate your dental insurance coverage and learn how to get the most value from your plan.',
  category: 'Insurance',
  date: 'May 15, 2026',
  readTime: '8 min read',
  image: 'https://images.unsplash.com/photo-1629909613654-28e377c37b09?auto=format&fit=crop&q=80&w=800',
  featured: false
}];
const categories = ['All', 'Oral Health', 'Orthodontics', 'Restorative', 'Cosmetic', 'Pediatric', 'Insurance'];
export function BlogPage() {
  useScreenInit();
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const filteredPosts = blogPosts.filter(post => {
    const matchesCategory = selectedCategory === 'All' || post.category === selectedCategory;
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase()) || post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });
  const featuredPosts = filteredPosts.filter(p => p.featured);
  const regularPosts = filteredPosts.filter(p => !p.featured);
  return <div className="min-h-screen bg-white" data-id="element-361">
      <Header data-id="element-362" />

      <main className="pt-40 pb-24" data-id="element-363">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-[#225274] to-[#4F709C] text-white py-20 px-4" data-id="element-364">
          <div className="max-w-4xl mx-auto text-center" data-id="element-365">
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-4xl md:text-6xl font-bold mb-6" data-id="element-366">
              Dental Health Blog
            </motion.h1>
            <motion.p initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.1
          }} className="text-xl text-white/90 mb-8" data-id="element-367">
              Expert insights, tips, and advice for maintaining your healthiest
              smile
            </motion.p>

            {/* Search Bar */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            delay: 0.2
          }} className="max-w-2xl mx-auto relative" data-id="element-368">
              <Search className="absolute left-6 top-1/2 -translate-y-1/2 text-slate-400" size={20} data-id="element-369" />
              <input type="text" placeholder="Search articles..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-14 pr-6 py-4 rounded-full text-slate-800 focus:outline-none focus:ring-2 focus:ring-[#54c595] transition-all" data-id="element-370" />
            </motion.div>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 mt-16" data-id="element-371">
          {/* Category Filter */}
          <div className="flex flex-wrap gap-3 mb-12 justify-center" data-id="element-372">
            {categories.map(category => <button key={category} onClick={() => setSelectedCategory(category)} className={`px-6 py-2 rounded-full font-bold text-sm transition-all ${selectedCategory === category ? 'bg-gradient-to-r from-[#225274] via-[#4bafbc] to-[#54c595] text-white shadow-lg' : 'bg-slate-100 text-slate-600 hover:bg-slate-200'}`} data-id="element-373">
                {category}
              </button>)}
          </div>

          {/* Featured Posts */}
          {featuredPosts.length > 0 && <div className="mb-20" data-id="element-374">
              <h2 className="text-3xl font-bold text-[#225274] mb-8" data-id="element-375">
                Featured Articles
              </h2>
              <div className="grid md:grid-cols-2 gap-8" data-id="element-376">
                {featuredPosts.map((post, index) => <Link key={post.id} to={`/blog/${post.id}`} className="block group" data-id="element-377">
                    <motion.article initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="cursor-pointer" data-id="element-378">
                      <div className="relative overflow-hidden rounded-[24px] mb-6 aspect-video" data-id="element-379">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-380" />
                        <div className="absolute top-4 left-4" data-id="element-381">
                          <span className="bg-[#54c595] text-white px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider" data-id="element-382">
                            Featured
                          </span>
                        </div>
                      </div>
                      <div className="flex items-center gap-4 mb-4 text-sm text-slate-500" data-id="element-383">
                        <span className="flex items-center gap-1.5" data-id="element-384">
                          <Tag size={14} className="text-[#54c595]" data-id="element-385" />
                          {post.category}
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-386">
                          <Calendar size={14} data-id="element-387" />
                          {post.date}
                        </span>
                        <span className="flex items-center gap-1.5" data-id="element-388">
                          <Clock size={14} data-id="element-389" />
                          {post.readTime}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold text-[#225274] mb-3 group-hover:text-[#54c595] transition-colors" data-id="element-390">
                        {post.title}
                      </h3>
                      <p className="text-slate-600 mb-4 leading-relaxed" data-id="element-391">
                        {post.excerpt}
                      </p>
                      <div className="text-[#54c595] font-bold text-sm flex items-center gap-2 group-hover:gap-3 transition-all" data-id="element-392">
                        Read More <ArrowRight size={16} data-id="element-393" />
                      </div>
                    </motion.article>
                  </Link>)}
              </div>
            </div>}

          {/* Regular Posts Grid */}
          {regularPosts.length > 0 && <div data-id="element-394">
              <h2 className="text-3xl font-bold text-[#225274] mb-8" data-id="element-395">
                Latest Articles
              </h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-396">
                {regularPosts.map((post, index) => <Link key={post.id} to={`/blog/${post.id}`} className="block group" data-id="element-397">
                    <motion.article initial={{
                opacity: 0,
                y: 20
              }} whileInView={{
                opacity: 1,
                y: 0
              }} viewport={{
                once: true
              }} transition={{
                delay: index * 0.1
              }} className="bg-white border border-slate-100 rounded-[24px] overflow-hidden hover:shadow-xl transition-all cursor-pointer" data-id="element-398">
                      <div className="relative overflow-hidden aspect-video" data-id="element-399">
                        <img src={post.image} alt={post.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" data-id="element-400" />
                      </div>
                      <div className="p-6" data-id="element-401">
                        <div className="flex items-center gap-3 mb-3 text-xs text-slate-500" data-id="element-402">
                          <span className="flex items-center gap-1" data-id="element-403">
                            <Tag size={12} className="text-[#54c595]" data-id="element-404" />
                            {post.category}
                          </span>
                          <span data-id="element-405">•</span>
                          <span className="flex items-center gap-1" data-id="element-406">
                            <Clock size={12} data-id="element-407" />
                            {post.readTime}
                          </span>
                        </div>
                        <h3 className="text-xl font-bold text-[#225274] mb-2 group-hover:text-[#54c595] transition-colors line-clamp-2" data-id="element-408">
                          {post.title}
                        </h3>
                        <p className="text-sm text-slate-600 mb-4 line-clamp-3" data-id="element-409">
                          {post.excerpt}
                        </p>
                        <div className="flex items-center justify-between" data-id="element-410">
                          <span className="text-xs text-slate-400" data-id="element-411">
                            {post.date}
                          </span>
                          <div className="text-[#54c595] font-bold text-xs flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-412">
                            Read <ArrowRight size={14} data-id="element-413" />
                          </div>
                        </div>
                      </div>
                    </motion.article>
                  </Link>)}
              </div>
            </div>}

          {/* No Results */}
          {filteredPosts.length === 0 && <div className="text-center py-20" data-id="element-414">
              <p className="text-xl text-slate-400" data-id="element-415">
                No articles found matching your criteria.
              </p>
            </div>}
        </div>
      </main>

      <Footer data-id="element-416" />
    </div>;
}